import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  BOOK_MASTER_URL, AUTHOR_MASTER_URL, CATEGORY_MASTER_URL,
  LANGUAGE_MASTER_URL, CLOSURE_REASON_MASTER_URL, MEMBER_MGMT_URL, USER_MGMT_URL, ROUTE_MASTER_URL, STATS_URL, TRANSACTION_URL,
  CENTERS_MASTER_URL, CENTER_TRNX_URL, BARCODE_LABEL_URL, BOOK_URL, BOOK_INVENTORY_URL, SYSPARAM_URL,
  MEMBER_URL, USER_URL, FINANCIAL_TRNX_URL, BOOK_CLAIMS_URL, ICT_URL, DAILY_SUMMARY_URL,
} from '../constants/api.constants';
import { AuthService } from './auth.service';
import { ConfigurationService } from './configuration.service';
import { MemberPieStats } from '../models';

@Injectable({ providedIn: 'root' })
export class ApiService {
  constructor(private http: HttpClient, private auth: AuthService, private config: ConfigurationService) { }

  private u(path: string): string {
    return this.config.apiBaseUrl + path;
  }

  private extractData<T>(res: any): T {
    if (res?.errorCode !== '000000') {
      const msg: string = res?.errorDescription || 'API request failed';
      // Backend rejects the session — clear stale token and redirect to login
      if (msg.includes('Autorization') || msg.includes('user identifier') || msg.includes('Relogin') || msg.includes('token')) {
        this.auth.logout();
      }
      throw new Error(msg);
    }
    return res?.Data as T;
  }

  // Framework reads GET params from req.query.criteria (JSON string)
  private get<T>(url: string, params?: Record<string, any>): Observable<T> {
    const opts = params && Object.keys(params).length > 0
      ? { params: new HttpParams().set('criteria', JSON.stringify(params)) }
      : {};
    return this.http.get<any>(url, opts).pipe(map(r => this.extractData<T>(r)));
  }

  // Framework reads POST body from req.body.criteria
  private post<T>(url: string, body: any): Observable<T> {
    return this.http.post<any>(url, { criteria: body }).pipe(map(r => this.extractData<T>(r)));
  }

  // Framework reads PUT body from req.body.criteria
  private put<T>(url: string, body: any): Observable<T> {
    return this.http.put<any>(url, { criteria: body }).pipe(map(r => this.extractData<T>(r)));
  }

  // Framework reads DELETE criteria from query string
  private delete<T>(url: string, params?: Record<string, any>): Observable<T> {
    const opts = params && Object.keys(params).length > 0
      ? { params: new HttpParams().set('criteria', JSON.stringify(params)) }
      : {};
    return this.http.delete<any>(url, opts).pipe(map(r => this.extractData<T>(r)));
  }

  // ── Books ──
  getBooks(params: any = {}): Observable<any[]> {
    return this.get<any[]>(this.u(`${BOOK_MASTER_URL}/getBooks`), params);
  }

  getBooksCount(params: any = {}): Observable<number> {
    return this.get<number>(this.u(`${BOOK_MASTER_URL}/getBooksCount`), params);
  }

  searchBooks(search: string, field?: string): Observable<any[]> {
    const params: any = { search };
    if (field) params['field'] = field;
    return this.get<any[]>(this.u(`${BOOK_URL}/searchBooks`), params);
  }

  getBookById(id: string): Observable<any> {
    return this.get<any>(this.u(`${BOOK_MASTER_URL}/getBookById`), { id });
  }

  getBookCatalogue(filter: 'all' | 'in-inventory' | 'latest', count?: number): Observable<any[]> {
    const params: any = { filter };
    if (count != null) params.count = count;
    return this.get<any[]>(this.u(`${BOOK_MASTER_URL}/getBookCatalogue`), params);
  }

  startBookCataloguePdf(filter: 'all' | 'in-inventory' | 'latest', count?: number): Observable<{ jobId: string }> {
    const params: any = { filter, clientName: this.auth.getCurrentUser()?.clientName || '' };
    if (count != null) params.count = count;
    return this.get<{ jobId: string }>(this.u(`${BOOK_MASTER_URL}/startBookCataloguePdf`), params);
  }

  getBookCataloguePdfStatus(jobId: string): Observable<{ status: 'processing' | 'done' | 'error'; error?: string }> {
    return this.get<{ status: 'processing' | 'done' | 'error'; error?: string }>(
      this.u(`${BOOK_MASTER_URL}/getBookCataloguePdfStatus`), { jobId });
  }

  downloadBookCataloguePdfResult(jobId: string): Observable<Blob> {
    return this.http.get(this.u(`${BOOK_MASTER_URL}/getBookCataloguePdfResult`), {
      params: new HttpParams().set('criteria', JSON.stringify({ jobId })),
      responseType: 'blob',
    });
  }

  saveBook(data: any): Observable<any> {
    return this.post<any>(this.u(`${BOOK_MASTER_URL}/saveBook`), data);
  }

  addCopies(data: any): Observable<any> {
    return this.post<any>(this.u(`${BOOK_MASTER_URL}/addCopies`), data);
  }

  updateBook(data: any): Observable<any> {
    return this.put<any>(this.u(`${BOOK_MASTER_URL}/updateBook`), data);
  }

  deleteBook(id: string): Observable<any> {
    return this.delete<any>(this.u(`${BOOK_MASTER_URL}/deleteBook`), { id });
  }

  // ── Authors ──
  getAuthors(params: any = {}): Observable<any[]> {
    return this.get<any[]>(this.u(`${AUTHOR_MASTER_URL}/getAuthors`), params);
  }

  getAuthorById(id: string): Observable<any> {
    return this.get<any>(this.u(`${AUTHOR_MASTER_URL}/getAuthorById`), { id });
  }

  saveAuthor(data: any): Observable<any> {
    return this.post<any>(this.u(`${AUTHOR_MASTER_URL}/saveAuthor`), data);
  }

  updateAuthor(data: any): Observable<any> {
    return this.put<any>(this.u(`${AUTHOR_MASTER_URL}/updateAuthor`), data);
  }

  deleteAuthor(id: string): Observable<any> {
    return this.delete<any>(this.u(`${AUTHOR_MASTER_URL}/deleteAuthor`), { id });
  }

  // ── Categories ──
  getCategories(params: any = {}): Observable<any[]> {
    return this.get<any[]>(this.u(`${CATEGORY_MASTER_URL}/getCategories`), params);
  }

  getCategoryById(id: string): Observable<any> {
    return this.get<any>(this.u(`${CATEGORY_MASTER_URL}/getCategoryById`), { id });
  }

  saveCategory(data: any): Observable<any> {
    return this.post<any>(this.u(`${CATEGORY_MASTER_URL}/saveCategory`), data);
  }

  updateCategory(data: any): Observable<any> {
    return this.put<any>(this.u(`${CATEGORY_MASTER_URL}/updateCategory`), data);
  }

  deleteCategory(id: string): Observable<any> {
    return this.delete<any>(this.u(`${CATEGORY_MASTER_URL}/deleteCategory`), { id });
  }

  // ── Languages ──
  getLanguages(params: any = {}): Observable<any[]> {
    return this.get<any[]>(this.u(`${LANGUAGE_MASTER_URL}/getLanguages`), params);
  }

  getLanguageById(id: string): Observable<any> {
    return this.get<any>(this.u(`${LANGUAGE_MASTER_URL}/getLanguageById`), { id });
  }

  saveLanguage(data: any): Observable<any> {
    return this.post<any>(this.u(`${LANGUAGE_MASTER_URL}/saveLanguage`), data);
  }

  updateLanguage(data: any): Observable<any> {
    return this.put<any>(this.u(`${LANGUAGE_MASTER_URL}/updateLanguage`), data);
  }

  deleteLanguage(id: string): Observable<any> {
    return this.delete<any>(this.u(`${LANGUAGE_MASTER_URL}/deleteLanguage`), { id });
  }

  // ── Membership Closure Reasons ──
  /** isActive: 'true' for the closure dropdown; omit for the master list (active + inactive). */
  getClosureReasons(params: any = {}): Observable<any[]> {
    return this.get<any[]>(this.u(`${CLOSURE_REASON_MASTER_URL}/getClosureReasons`), params);
  }

  saveClosureReason(data: any): Observable<any> {
    return this.post<any>(this.u(`${CLOSURE_REASON_MASTER_URL}/saveClosureReason`), data);
  }

  updateClosureReason(data: any): Observable<any> {
    return this.put<any>(this.u(`${CLOSURE_REASON_MASTER_URL}/updateClosureReason`), data);
  }

  // ── Members ──
  getMembers(params: any = {}): Observable<any[]> {
    return this.get<any[]>(this.u(`${MEMBER_MGMT_URL}/getMembers`), params);
  }

  getMemberAreas(): Observable<string[]> {
    return this.get<string[]>(this.u(`${MEMBER_MGMT_URL}/getMemberAreas`), {});
  }

  getMemberById(memberId: string): Observable<any> {
    return this.get<any>(this.u(`${MEMBER_URL}/getMemberById`), { memberId });
  }

  getMemberBySrNo(memberSrNo: number | string): Observable<any> {
    return this.get<any>(this.u(`${MEMBER_URL}/getMemberBySrNo`), { memberSrNo });
  }

  saveMember(data: any): Observable<any> {
    return this.post<any>(this.u(`${MEMBER_MGMT_URL}/saveMember`), data);
  }

  updateMember(data: any): Observable<any> {
    return this.put<any>(this.u(`${MEMBER_MGMT_URL}/updateMember`), data);
  }

  renewMembership(id: string, months: number, amount?: number, startDate?: string, paymentData?: { paymentMode?: string; paymentTrnxRef?: string; paymentDate?: string; paymentRemarks?: string }): Observable<any> {
    return this.post<any>(this.u(`${MEMBER_MGMT_URL}/renewMembership`), { id, months, amount, startDate, ...(paymentData || {}) });
  }

  closeMembership(id: string, refundData?: {
    refundMode?: string; refundTrnxId?: string; refundDate?: string; refundRemarks?: string;
    refundAmount?: number; returnedTo?: string; returnedToPhone?: string; closureReason?: string; closureReasonId?: string;
  }): Observable<any> {
    return this.put<any>(this.u(`${MEMBER_MGMT_URL}/closeMembership`), { id, ...(refundData || {}) });
  }

  setMemberStatus(id: string, newStatus: string, onHoldTill?: string): Observable<any> {
    return this.put<any>(this.u(`${MEMBER_MGMT_URL}/setMemberStatus`), { id, newStatus, onHoldTill });
  }

  getFinancialTransactionsByMember(memberId: string): Observable<any[]> {
    return this.get<any[]>(this.u(`${FINANCIAL_TRNX_URL}/getFinancialTransactionsByMember`), { memberId });
  }

  // ── Book Claims ──
  addBookClaim(data: any): Observable<any> {
    return this.post<any>(this.u(`${BOOK_CLAIMS_URL}/addClaim`), data);
  }

  getBookClaims(params: any = {}): Observable<any[]> {
    return this.get<any[]>(this.u(`${BOOK_CLAIMS_URL}/getClaims`), params);
  }

  getClaimsForMember(memberId: string, status?: string): Observable<any[]> {
    return this.get<any[]>(this.u(`${BOOK_CLAIMS_URL}/getClaimsForMember`), { memberId, ...(status ? { status } : {}) });
  }

  getClaimsForBook(bookId: string): Observable<any[]> {
    return this.get<any[]>(this.u(`${BOOK_CLAIMS_URL}/getClaimsForBook`), { bookId });
  }

  getAvailableCopiesForClaim(bookId: string, centerId?: string): Observable<any[]> {
    return this.get<any[]>(this.u(`${BOOK_CLAIMS_URL}/getAvailableCopiesForBook`),
      { bookId, ...(centerId ? { centerId } : {}) });
  }

  allocateClaim(claimId: string, bookSrNo: number): Observable<any> {
    return this.put<any>(this.u(`${BOOK_CLAIMS_URL}/allocateClaim`), { claimId, bookSrNo });
  }

  cancelClaim(claimId: string, reason?: string): Observable<any> {
    return this.put<any>(this.u(`${BOOK_CLAIMS_URL}/cancelClaim`), { claimId, reason: reason || '' });
  }

  updateQuickAccess(userId: string, routes: string[]): Observable<any> {
    return this.put<any>(this.u(`${USER_URL}/quickAccess`), { userId, routes });
  }

  saveUserTheme(userId: string, theme: string): Observable<any> {
    return this.http.patch<any>(this.u(`${USER_URL}/theme`), { criteria: { userId, theme } }).pipe(map(r => this.extractData<any>(r)));
  }

  changePassword(userId: string, currentPassword: string, newPassword: string): Observable<any> {
    return this.put<any>(this.u(`${USER_URL}/changePassword`), { userId, currentPassword, newPassword });
  }

  deleteMember(id: string): Observable<any> {
    return this.delete<any>(this.u(`${MEMBER_MGMT_URL}/deleteMember`), { id });
  }

  refreshMembershipStatus(): Observable<{ movedToMember: number; movedToDue: number; movedToHold: number }> {
    return this.post<{ movedToMember: number; movedToDue: number; movedToHold: number }>(this.u(`${MEMBER_MGMT_URL}/refreshMembershipStatus`), {});
  }

  loginMembershipCheck(): Observable<{ movedToDue: number; movedToHold: number; skipped?: boolean }> {
    return this.post<{ movedToDue: number; movedToHold: number; skipped?: boolean }>(this.u(`${MEMBER_MGMT_URL}/loginMembershipCheck`), {});
  }

  // ── Users ──
  getUsers(params: any = {}): Observable<any[]> {
    return this.get<any[]>(this.u(`${USER_MGMT_URL}/getUsers`), params);
  }

  getRunners(): Observable<any[]> {
    return this.get<any[]>(this.u(`${USER_MGMT_URL}/getRunners`));
  }

  getUserById(id: string): Observable<any> {
    return this.get<any>(this.u(`${USER_MGMT_URL}/getUserById`), { id });
  }

  saveUser(data: any): Observable<any> {
    return this.post<any>(this.u(`${USER_MGMT_URL}/saveUser`), data);
  }

  updateUser(data: any): Observable<any> {
    return this.put<any>(this.u(`${USER_MGMT_URL}/updateUser`), data);
  }

  deleteUser(id: string): Observable<any> {
    return this.delete<any>(this.u(`${USER_MGMT_URL}/deleteUser`), { id });
  }

  assignCentersToManager(managerId: string, centerIds: string[]): Observable<any> {
    return this.put<any>(this.u(`${USER_MGMT_URL}/assignCentersToManager`), { managerId, centerIds });
  }

  // ── Route Master ──
  getRouteMasters(params: any = {}): Observable<any[]> {
    return this.get<any[]>(this.u(`${ROUTE_MASTER_URL}/getRouteMasters`), params);
  }

  getRoutesByRunner(runnerId: string): Observable<any[]> {
    return this.get<any[]>(this.u(`${ROUTE_MASTER_URL}/getRoutesByRunner`), { runnerId });
  }

  getRouteMasterById(id: string): Observable<any> {
    return this.get<any>(this.u(`${ROUTE_MASTER_URL}/getRouteMasterById`), { id });
  }

  saveRouteMaster(data: any): Observable<any> {
    return this.post<any>(this.u(`${ROUTE_MASTER_URL}/saveRouteMaster`), data);
  }

  updateRouteMaster(data: any): Observable<any> {
    return this.put<any>(this.u(`${ROUTE_MASTER_URL}/updateRouteMaster`), data);
  }

  deleteRouteMaster(id: string): Observable<any> {
    return this.delete<any>(this.u(`${ROUTE_MASTER_URL}/deleteRouteMaster`), { id });
  }

  // ── Centers ──
  getCenters(params: any = {}): Observable<any[]> {
    return this.get<any[]>(this.u(`${CENTERS_MASTER_URL}/getCenters`), params);
  }

  getCenterById(id: string): Observable<any> {
    return this.get<any>(this.u(`${CENTERS_MASTER_URL}/getCenterById`), { id });
  }

  saveCenter(data: any): Observable<any> {
    return this.post<any>(this.u(`${CENTERS_MASTER_URL}/saveCenter`), data);
  }

  updateCenter(data: any): Observable<any> {
    return this.put<any>(this.u(`${CENTERS_MASTER_URL}/updateCenter`), data);
  }

  deleteCenter(id: string): Observable<any> {
    return this.delete<any>(this.u(`${CENTERS_MASTER_URL}/deleteCenter`), { id });
  }

  getManagers(): Observable<any[]> {
    return this.get<any[]>(this.u(`${USER_MGMT_URL}/getManagers`));
  }

  getCentersByManager(managerId: string): Observable<any[]> {
    return this.get<any[]>(this.u(`${CENTERS_MASTER_URL}/getCentersByManager`), { managerId });
  }

  // ── Center Transactions ──
  saveCenterTransaction(data: any): Observable<any> {
    return this.post<any>(this.u(`${CENTER_TRNX_URL}/saveCenterTransaction`), data);
  }

  getCenterTransactions(params: any = {}): Observable<any[]> {
    return this.get<any[]>(this.u(`${CENTER_TRNX_URL}/getCenterTransactions`), params);
  }

  // ── Transaction History ──
  getBookHistory(bookId: string): Observable<any[]> {
    return this.get<any[]>(this.u(`${TRANSACTION_URL}/getBookHistory`), { bookId });
  }

  getMemberTransactionHistory(memberId: string): Observable<any[]> {
    return this.get<any[]>(this.u(`${TRANSACTION_URL}/getMemberTransactionHistory`), { memberId });
  }

  getTransactionsByMember(memberId: string): Observable<any[]> {
    return this.get<any[]>(this.u(`${TRANSACTION_URL}/getTransactionHistory`), { memberId });
  }

  syncTransaction(data: any): Observable<any> {
    return this.post<any>(this.u(`${TRANSACTION_URL}/syncTransaction`), data);
  }

  validateBookAvailability(bookSrNo: string): Observable<any> {
    return this.post<any>(this.u(`${BOOK_URL}/validateBookAvailability`), { bookSrNo });
  }

  locateBook(search: string): Observable<any[]> {
    return this.get<any[]>(this.u(`${BOOK_URL}/locateBook`), { search });
  }

  locateBooksByAuthor(authorName: string): Observable<any[]> {
    return this.get<any[]>(this.u(`${BOOK_URL}/locateBook`), { authorName });
  }

  locateBooksByCategory(categoryName: string): Observable<any[]> {
    return this.get<any[]>(this.u(`${BOOK_URL}/locateBook`), { categoryName });
  }

  getBooksByCategory(categoryName: string): Observable<any[]> {
    return this.get<any[]>(this.u(`${BOOK_URL}/getBooksByCategory`), { categoryName });
  }

  getBookCopies(bookId: string): Observable<any[]> {
    return this.get<any[]>(this.u(`${BOOK_URL}/getBookCopies`), { bookId });
  }

  // ── Book Inventory ──
  getAllBookInventory(params: any = {}): Observable<any[]> {
    return this.get<any[]>(this.u(`${BOOK_INVENTORY_URL}/getAllInventory`), params);
  }

  // ── Barcode Labels ──
  generateLabels(data: any): Observable<any> {
    return this.post<any>(this.u(`${BARCODE_LABEL_URL}/generateLabels`), data);
  }

  getBookInventory(bookId: string): Observable<number[]> {
    return this.get<number[]>(this.u(`${BARCODE_LABEL_URL}/getBookInventory`), { bookId });
  }

  // ── SysParam ──
  getSubscriptionPlans(): Observable<any[]> {
    return this.get<any[]>(this.u(`${SYSPARAM_URL}/getSubscriptionPlans`));
  }

  getGeneralSettings(): Observable<any> {
    return this.get<any>(this.u(`${SYSPARAM_URL}/getGeneralSettings`));
  }

  updateGeneralSettings(depositAmount: number): Observable<any> {
    return this.post<any>(this.u(`${SYSPARAM_URL}/updateGeneralSettings`), { depositAmount });
  }

  updateSubscriptionPlans(plans: any[]): Observable<any[]> {
    return this.post<any[]>(this.u(`${SYSPARAM_URL}/updateSubscriptionPlans`), { plans });
  }

  getLabelLayouts(): Observable<any[]> {
    return this.get<any[]>(this.u(`${SYSPARAM_URL}/getLabelLayouts`));
  }

  saveLabelLayouts(layouts: any[]): Observable<any[]> {
    return this.post<any[]>(this.u(`${SYSPARAM_URL}/saveLabelLayouts`), { layouts });
  }

  getLabelNameSource(): Observable<{ source: string; customText: string }> {
    return this.get<any>(this.u(`${SYSPARAM_URL}/getLabelNameSource`));
  }

  saveLabelNameSource(source: string, customText: string): Observable<{ source: string; customText: string }> {
    return this.post<any>(this.u(`${SYSPARAM_URL}/saveLabelNameSource`), { source, customText });
  }

  getMemberCodeSettings(): Observable<{ homeDeliveryPrefix: string; centerPrefix: string }> {
    return this.get<any>(this.u(`${SYSPARAM_URL}/getMemberCodeSettings`));
  }

  updateMemberCodeSettings(homeDeliveryPrefix: string, centerPrefix: string): Observable<{ homeDeliveryPrefix: string; centerPrefix: string }> {
    return this.post<any>(this.u(`${SYSPARAM_URL}/updateMemberCodeSettings`), { homeDeliveryPrefix, centerPrefix });
  }

  getClientCode(): Observable<{ clientCode: string }> {
    return this.get<{ clientCode: string }>(this.u(`${SYSPARAM_URL}/getClientCode`));
  }

  updateClientCode(newClientCode: string): Observable<{ clientCode: string }> {
    return this.post<{ clientCode: string }>(this.u(`${SYSPARAM_URL}/updateClientCode`), { newClientCode });
  }

  getDateFormat(): Observable<{ dateFormat: string }> {
    return this.get<{ dateFormat: string }>(this.u(`${SYSPARAM_URL}/getDateFormat`));
  }

  saveDateFormat(dateFormat: string): Observable<{ dateFormat: string }> {
    return this.post<{ dateFormat: string }>(this.u(`${SYSPARAM_URL}/saveDateFormat`), { dateFormat });
  }

  // ── Stats ──
  getAdminStats(): Observable<any> {
    return this.get<any>(this.u(`${STATS_URL}/getAdminStats`));
  }

  getMonthlyMemberStats(): Observable<any[]> {
    return this.get<any[]>(this.u(`${STATS_URL}/getMonthlyMemberStats`));
  }

  getMemberPieStats(): Observable<MemberPieStats> {
    return this.get<MemberPieStats>(this.u(`${STATS_URL}/getMemberPieStats`));
  }

  getRouteBarStats(): Observable<{ routeId: string; locationId: string; description: string; member: number; due: number; onHold: number }[]> {
    return this.get(this.u(`${STATS_URL}/getRouteBarStats`));
  }

  getTodaySnapshot(): Observable<{ issuedToday: number; returnedToday: number; transactionsToday: number; newMembersToday: number }> {
    return this.get(this.u(`${STATS_URL}/getTodaySnapshot`));
  }

  getStatDetail(type: string, extra: Record<string, any> = {}): Observable<any[]> {
    return this.get<any[]>(this.u(`${STATS_URL}/getStatDetail`), { type, ...extra });
  }

  ictValidateForIssue(bookSrNo: string, centerId: string): Observable<any> {
    return this.get<any>(this.u(`${ICT_URL}/validateForIssue`), { bookSrNo, centerId });
  }

  ictLookupCopy(bookSrNo: string): Observable<any> {
    return this.get<any>(this.u(`${ICT_URL}/lookupCopy`), { bookSrNo });
  }

  ictExecute(payload: any): Observable<any> {
    return this.post<any>(this.u(`${ICT_URL}/execute`), payload);
  }

  ictGetHistory(primaryCenterId?: string): Observable<any[]> {
    return this.get<any[]>(this.u(`${ICT_URL}/history`), primaryCenterId ? { primaryCenterId } : {});
  }

  getDailySummary(centerId: string, fromDate: string, toDate: string): Observable<any> {
    return this.get<any>(this.u(`${DAILY_SUMMARY_URL}/summary`), { centerId, fromDate, toDate });
  }

  getFinancialTransactionReport(params: { fromDate: string; toDate: string; centerId?: string }): Observable<any[]> {
    return this.get<any[]>(this.u(`${FINANCIAL_TRNX_URL}/getFinancialTransactionReport`), params);
  }
}
